/**
 * 扑克牌核心模块
 * 提供扑克牌游戏所需的基础类、常量和工具函数
 * 包含：花色/点数定义、单张牌类(Card)、牌组类(Deck)、排序与统计工具函数
 */

// 花色定义
export const SUITS = {
  SPADES:   { name: 'spades',   symbol: '♠', color: 'black', code: 's' }, // 黑桃
  HEARTS:   { name: 'hearts',   symbol: '♥', color: 'red',   code: 'h' }, // 红桃
  CLUBS:    { name: 'clubs',    symbol: '♣', color: 'black', code: 'c' }, // 梅花
  DIAMONDS: { name: 'diamonds', symbol: '♦', color: 'red',   code: 'd' },  // 方块
  JOKER:    { name: 'joker',    symbol: '🃏', color: 'black', code: 'j' }  // 鬼牌
};

// 点数定义
export const RANKS = {
  ACE:   { name: 'A',  value: 1,  display: 'A' },  // A
  TWO:   { name: '2',  value: 2,  display: '2' },  // 2
  THREE: { name: '3',  value: 3,  display: '3' },  // 3
  FOUR:  { name: '4',  value: 4,  display: '4' },  // 4
  FIVE:  { name: '5',  value: 5,  display: '5' },  // 5
  SIX:   { name: '6',  value: 6,  display: '6' },  // 6
  SEVEN: { name: '7',  value: 7,  display: '7' },  // 7
  EIGHT: { name: '8',  value: 8,  display: '8' },  // 8
  NINE:  { name: '9',  value: 9,  display: '9' },  // 9
  TEN:   { name: '10', value: 10, display: '10' }, // 10
  JACK:  { name: 'J',  value: 11, display: 'J' },  // J（杰克/侍从）
  QUEEN: { name: 'Q',  value: 12, display: 'Q' },  // Q（皇后）
  KING:  { name: 'K',  value: 13, display: 'K' },   // K（国王）
  JOKER_RANK: { name: 'Joker', value: 0, display: '鬼' }  // 鬼牌/百搭牌
};

// 牌型等级（与参考方案一致，值越大牌型越大）
export const HAND_RANKS = {
  FIVE_OF_A_KIND: { name: '五同', value: 10 },   // 五条
  ROYAL_FLUSH: { name: '同花顺', value: 9 },
  FOUR_OF_A_KIND: { name: '炸弹', value: 8 },
  FULL_HOUSE: { name: '葫芦', value: 7 },
  FLUSH: { name: '同花', value: 6 },
  STRAIGHT: { name: '顺子', value: 5 },
  THREE_OF_A_KIND: { name: '三条', value: 4 },
  TWO_PAIRS: { name: '两对', value: 3 },
  ONE_PAIR: { name: '一对', value: 2 },
  HIGH_CARD: { name: '乌龙', value: 1 }
};

// 特殊牌型
export const SPECIAL_HANDS = {
  DRAGON: { name: '一条龙', score: 15 },                  // A-K各一张
  ALL_BIG: { name: '全大', score: 10 },                    // 全部为10-A
  ALL_SMALL: { name: '全小', score: 10 },                  // 全部为2-9
  SIX_PAIRS_HALF: { name: '六对半', score: 7 },           // 六对 + 一张单牌
  THREE_FLUSH: { name: '三同花', score: 7 },              // 三墩均为同花
  THREE_STRAIGHT: { name: '三顺子', score: 7 }            // 三墩均为顺子
};

// 单张牌类
export class Card {
  constructor(suit, rank, jokerType, copyIndex = 0) {
    this.suit = suit;      // 花色对象
    this.rank = rank;      // 点数对象
    this.jokerType = jokerType; // 'big' 或 'small'，仅鬼牌使用
    this.copyIndex = copyIndex; // 花牌副数序号，0 表示标准牌组
    this.id = this.buildId(suit, rank, jokerType, copyIndex)
  }

  buildId(suit, rank, jokerType, copyIndex = 0) {
    if (rank.name === 'Joker' || suit.name === 'joker') {
      return jokerType === 'small' ? 'js' : 'jb'
    }
    const base = `${suit.code}${rank.value}`
    return copyIndex > 0 ? `${base}@${copyIndex}` : base
  }

  /**
   * 获取牌的显示文本
   * @returns {string} 如 "♠A"、"♥10"
   */
  get display() {
    return `${this.suit.symbol}${this.rank.display}`;
  }

  /**
   * 获取牌用于比较的值
   * @returns {number} 点数数值（1-13）
   */
  get compareValue() {
    return this.rank.value;
  }

  /**
   * 判断该牌是否为 A
   * @returns {boolean}
   */
  get isAce() {
    return Number(this.rank.value) === 1;
  }

  /**
   * 判断该牌是否为鬼牌（百搭牌）
   * @returns {boolean}
   */
  get isJoker() {
    return this.rank.name === 'Joker' || this.suit.name === 'joker';
  }

  /**
   * 判断该牌是否为天然牌（非鬼牌）
   * @returns {boolean}
   */
  get isNatural() {
    return !this.isJoker;
  }

  /**
   * 克隆当前牌，返回一张新的 Card 实例
   * @returns {Card}
   */
  clone() {
    const card = new Card(this.suit, this.rank, this.jokerType, this.copyIndex || 0)
    card.id = this.id
    return card
  }

  /** 构造后若才设置 jokerType，需同步 id（jb/js），避免大小鬼 id 重复 */
  setJokerType(type) {
    this.jokerType = type
    if (this.isJoker) {
      this.id = this.buildId(this.suit, this.rank, type, this.copyIndex || 0)
    }
    return this
  }

  /**
   * 将牌转换为可序列化的简单对象
   * @returns {{suit: string, rank: number, id: string, jokerType: string}}
   */
  toJSON() {
    return {
      suit: this.suit.name,
      rank: this.rank.value,
      id: this.id,
      jokerType: this.jokerType
    };
  }

  /**
   * 从序列化对象还原为 Card 实例
   * @param {{suit: string, rank: number, id: string, jokerType: string}} json - 序列化对象
   * @returns {Card}
   */
  static fromJSON(json) {
    const suit = resolveSuitFromPayload(json.suit);
    const rank = resolveRankFromPayload(json.rank);
    const card = new Card(suit, rank, json.jokerType, json.copyIndex || 0);
    if (json.id) {
      card.id = json.id
    }
    return card;
  }
}

/** 判断是否为鬼牌（支持 Card 实例与接口/序列化 plain object） */
export function isJokerCard(card) {
  if (!card) return false
  if (card instanceof Card) return card.isJoker
  if (typeof card.isJoker === 'boolean') return card.isJoker
  const suitRaw = card.suit
  const rankRaw = card.rank
  const suitName = typeof suitRaw === 'string'
    ? suitRaw.toLowerCase()
    : (suitRaw?.name || '').toLowerCase()
  if (suitName === 'joker') return true
  const rankVal = typeof rankRaw === 'number' ? rankRaw : rankRaw?.value
  const rankName = typeof rankRaw === 'string' ? rankRaw : rankRaw?.name
  if (rankVal === 0) return true
  if (rankName === 'Joker' || rankName === 'JOKER') return true
  if (card.id === 'jb' || card.id === 'js') return true
  return false
}

function resolveSuitFromPayload(suitPayload) {
  if (suitPayload && typeof suitPayload === 'object' && suitPayload.name) {
    return Object.values(SUITS).find(s => s.name === suitPayload.name) || SUITS.SPADES
  }
  const key = String(suitPayload || '').toLowerCase()
  if (key === 'joker') return SUITS.JOKER
  const byName = Object.values(SUITS).find(s => s.name === key)
  if (byName) return byName
  const enumMap = {
    spades: SUITS.SPADES,
    hearts: SUITS.HEARTS,
    clubs: SUITS.CLUBS,
    diamonds: SUITS.DIAMONDS,
    joker: SUITS.JOKER
  }
  return enumMap[key] || SUITS.SPADES
}

function resolveRankFromPayload(rankPayload) {
  if (rankPayload && typeof rankPayload === 'object' && rankPayload.value !== undefined) {
    if (rankPayload.name === 'Joker' || rankPayload.value === 0) return RANKS.JOKER_RANK
    return Object.values(RANKS).find(r => r.value === rankPayload.value) || RANKS.ACE
  }
  if (typeof rankPayload === 'string') {
    if (rankPayload.toUpperCase() === 'JOKER') return RANKS.JOKER_RANK
    const byName = Object.values(RANKS).find(r => r.name === rankPayload)
    if (byName) return byName
  }
  if (typeof rankPayload === 'number') {
    if (rankPayload === 0) return RANKS.JOKER_RANK
    return Object.values(RANKS).find(r => r.value === rankPayload) || RANKS.ACE
  }
  return RANKS.ACE
}

/** 比牌/算牌前统一转为 Card，避免 layout 里是 plain object 时鬼牌未参与百搭 */
export function normalizeCardForEval(card) {
  if (!card) return card
  if (card instanceof Card) return card
  if (card.suit && typeof card.suit === 'object' && card.rank && typeof card.rank === 'object') {
    const normalized = new Card(card.suit, card.rank, card.jokerType, card.copyIndex || 0)
    if (card.id) normalized.id = card.id
    return normalized
  }
  return Card.fromJSON(card)
}

export function normalizeCardsForEval(cards) {
  if (!Array.isArray(cards)) return []
  return cards.map(normalizeCardForEval)
}

// 牌组类
export class Deck {
  constructor() {
    this.cards = [];
    this.reset();
  }

  /**
   * 重置牌组，重新生成一副完整的52张牌
   */
  reset() {
    this.cards = [];
    const suits = Object.values(SUITS).filter(s => s.name !== 'joker');
    const ranks = Object.values(RANKS).filter(r => r.name !== 'Joker');

    for (const suit of suits) {
      for (const rank of ranks) {
        this.cards.push(new Card(suit, rank));
      }
    }
  }

  /**
   * 洗牌，使用 Fisher-Yates 算法打乱牌序
   */
  shuffle() {
    for (let i = this.cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
    }
  }

  /**
   * 从牌组顶部发牌
   * @param {number} [count=1] - 发牌数量
   * @returns {Card|Card[]} 发一张时返回 Card，多张时返回 Card 数组
   */
  deal(count = 1) {
    if (count === 1) {
      return this.cards.pop();
    }
    return this.cards.splice(-count);
  }

  /**
   * 获取牌组剩余牌数
   * @returns {number}
   */
  get remaining() {
    return this.cards.length;
  }

  /**
   * 判断牌组是否已发完
   * @returns {boolean}
   */
  get isEmpty() {
    return this.cards.length === 0;
  }
}

/**
 * 按点数排序牌组（默认降序，A 视为最大）
 * @param {Card[]} cards - 待排序的牌数组
 * @param {boolean} [ascending=false] - 是否升序排列，默认降序
 * @returns {Card[]} 排序后的新数组（不修改原数组）
 */
export function sortByRank(cards, ascending = false) {
  return [...cards].sort((a, b) => {
    // A作为14点进行比较
    const valA = Number(a.rank.value) === 1 ? 14 : Number(a.rank.value);
    const valB = Number(b.rank.value) === 1 ? 14 : Number(b.rank.value);
    return ascending ? valA - valB : valB - valA;
  });
}

/**
 * 将 13 张手牌拆成展示用三墩（仅用于特殊牌型展示，不参与比牌）。
 * 尾道取最大的 5 张，中道取中间 5 张，头道取最小的 3 张。
 */
export function buildSpecialHandDisplayLayout(hands) {
  if (!Array.isArray(hands) || hands.length !== 13) {
    return { front: [], middle: [], back: [] }
  }
  const sorted = sortByRank(hands)
  return {
    back: sorted.slice(0, 5),
    middle: sorted.slice(5, 10),
    front: sorted.slice(10, 13)
  }
}

/**
 * 按花色排序牌组（花色顺序：黑桃、红桃、梅花、方块），同花色内按点数降序
 * @param {Card[]} cards - 待排序的牌数组
 * @returns {Card[]} 排序后的新数组（不修改原数组）
 */
export function sortBySuit(cards) {
  const suitOrder = ['spades', 'hearts', 'clubs', 'diamonds'];
  return [...cards].sort((a, b) => {
    const suitDiff = suitOrder.indexOf(a.suit.name) - suitOrder.indexOf(b.suit.name);
    if (suitDiff !== 0) return suitDiff;
    const valA = Number(a.rank.value) === 1 ? 14 : Number(a.rank.value);
    const valB = Number(b.rank.value) === 1 ? 14 : Number(b.rank.value);
    return valB - valA;
  });
}

/**
 * 统计牌组中各点数出现的次数
 * @param {Card[]} cards - 牌数组
 * @returns {Object.<number, number>} 键为点数数值，值为出现次数
 */
export function getRankCounts(cards) {
  const counts = {};
  for (const card of cards) {
    if (isJokerCard(card)) continue
    const val = card.rank.value;
    counts[val] = (counts[val] || 0) + 1;
  }
  return counts;
}

/**
 * 统计牌组中各花色出现的次数
 * @param {Card[]} cards - 牌数组
 * @returns {Object.<string, number>} 键为花色名称，值为出现次数
 */
export function getSuitCounts(cards) {
  const counts = {};
  for (const card of cards) {
    if (isJokerCard(card)) continue
    const suit = card.suit.name;
    counts[suit] = (counts[suit] || 0) + 1;
  }
  return counts;
}
